import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ClipboardX, FileText, Loader2, Save, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import StaffAdminEval from "@/components/StaffAdminEval";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCORES = [1, 2, 3, 4, 5];
const EV_STATUS = {
  pending: ["بانتظار مراجعة المدير", "bg-amber-100 text-amber-800 border-amber-300"],
  accepted: ["مقبول", "bg-emerald-100 text-emerald-800 border-emerald-300"],
  rejected: ["مرفوض", "bg-rose-100 text-rose-700 border-rose-300"],
};

export default function StaffEvaluation({ staffFetch, employee, token }) {
  const [period, setPeriod] = useState("الفصل الأول");
  const [data, setData] = useState(null);
  const [scores, setScores] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const fileInputs = useRef({});

  const load = useCallback(async (p) => {
    try {
      const d = await staffFetch(`/staff/evaluation?period=${encodeURIComponent(p)}`);
      setData(d);
      setScores(d.self?.scores || {});
    } catch (e) {
      if (e.message !== "session-ended") toast.error(e.message);
    }
  }, [staffFetch]);

  useEffect(() => { setData(null); load(period); }, [period, load]);

  const totals = useMemo(() => {
    if (!data?.has_form) return null;
    const crit = data.form.criteria;
    const wsum = crit.reduce((a, c) => a + c.weight, 0) || 1;
    const t5 = crit.reduce((a, c, i) => a + (Number(scores[String(i + 1)]) || 0) * c.weight, 0) / wsum;
    return { total: Math.round(t5 * 100) / 100, pct: Math.round(t5 / 5 * 1000) / 10 };
  }, [data, scores]);

  const save = async () => {
    setSaving(true);
    try {
      const d = await staffFetch("/staff/evaluation", {
        method: "PUT", body: JSON.stringify({ period, scores }),
      });
      setData((prev) => ({ ...prev, self: d }));
      toast.success(`حُفظ تقييمك الذاتي — النتيجة ${d.pct}٪`);
    } catch (e) {
      if (e.message !== "session-ended") toast.error(e.message);
    } finally { setSaving(false); }
  };

  const upload = async (criterion, idx, files) => {
    const list = [...(files || [])];
    if (!list.length) return;
    const key = `${criterion}-${idx}`;
    setUploading(key);
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append("period", period);
        fd.append("criterion", String(criterion));
        fd.append("evidence_idx", String(idx));
        fd.append("file", file);
        const res = await fetch(`${API}/staff/evidence/upload`, {
          method: "POST", headers: { "X-Staff-Token": token }, body: fd,
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(d.detail || "فشل الرفع");
      }
      toast.success(list.length > 1
        ? `تم رفع ${list.length} شواهد — بانتظار مراجعة المدير`
        : "تم رفع الشاهد — بانتظار مراجعة المدير");
      await load(period);
    } catch (e) {
      toast.error(typeof e.message === "string" ? e.message : "فشل الرفع");
    } finally {
      setUploading("");
      if (fileInputs.current[key]) fileInputs.current[key].value = "";
    }
  };

  const removeEvidence = async (evId) => {
    try {
      const res = await fetch(`${API}/staff/evidence/${evId}`, {
        method: "DELETE", headers: { "X-Staff-Token": token },
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "تعذر الحذف");
      toast.success("حُذف الشاهد");
      await load(period);
    } catch (e) { toast.error(e.message); }
  };

  const viewFile = async (evId) => {
    try {
      const res = await fetch(`${API}/staff/evidence/file/${evId}`, { headers: { "X-Staff-Token": token } });
      if (!res.ok) throw new Error("تعذر فتح الملف");
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) { toast.error(e.message); }
  };

  if (!data) return <p className="text-center text-sm text-slate-400 py-10">جارٍ التحميل...</p>;

  if (!data.has_form && data.job === "إداري") {
    return <StaffAdminEval staffFetch={staffFetch} employee={employee} token={token} />;
  }

  if (!data.has_form) {
    return (
      <div className="bg-white rounded-2xl border p-8 text-center space-y-2" data-testid="staff-eval-no-form">
        <ClipboardX className="w-8 h-8 mx-auto text-slate-300" />
        <p className="text-sm font-bold text-slate-600">{data.note}</p>
        <p className="text-[11px] text-slate-400">المسمى الحالي: {data.job || "غير محدد"}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="staff-evaluation">
      <div className="bg-white rounded-2xl border p-3 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-bold text-slate-500">الفترة:</span>
        {data.periods.map((p) => (
          <button key={p} onClick={() => setPeriod(p)} data-testid={`staff-eval-period-${p}`}
            className={`rounded-full px-3 py-1.5 text-[11px] font-bold border transition-colors ${
              period === p ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-slate-600"}`}>
            {p}
          </button>
        ))}
        <span className="text-[10px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1"
          data-testid="staff-eval-year">{data.year}</span>
      </div>

      <div className="bg-emerald-950 text-white rounded-2xl px-4 py-3">
        <p className="font-extrabold text-sm" data-testid="staff-eval-title">{data.form.title} — تقييمي الذاتي</p>
        <p className="text-[11px] text-emerald-300/80 mt-0.5">
          {employee.name} — {data.job} | {period}
          {data.manager_total && (
            <span className="mr-2 text-amber-300" data-testid="staff-eval-manager-total">
              | تقييم المدير: {data.manager_total.pct}٪
            </span>
          )}
        </p>
      </div>

      <div className="bg-white rounded-2xl border divide-y" data-testid="staff-eval-form">
        {data.form.criteria.map((c, i) => {
          const k = String(i + 1);
          const evGroup = data.evidence?.find((g) => g.criterion === i + 1);
          return (
            <div key={k} className="px-4 py-3" data-testid={`staff-eval-criterion-${k}`}>
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <p className="flex-1 min-w-[160px] text-xs font-semibold leading-6">{c.name}</p>
                <span className="text-[10px] font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{c.weight}٪</span>
                <div className="flex gap-1" data-testid={`staff-eval-score-${k}`}>
                  {SCORES.map((s) => (
                    <button key={s} onClick={() => setScores({ ...scores, [k]: s })}
                      data-testid={`staff-eval-score-${k}-${s}`}
                      className={`w-8 h-8 rounded-lg border text-xs font-extrabold transition-colors ${
                        Number(scores[k]) === s ? "bg-emerald-900 text-white border-emerald-900" : "bg-white hover:bg-emerald-50"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              {i === 0 && data.criterion1 && (
                <div className="mt-2 mr-8 space-y-1.5" data-testid="staff-criterion1-auto">
                  <p className="text-[10px] font-bold text-slate-400">بنود تُقيَّم آلياً من سجلاتك:</p>
                  {data.criterion1.items.map((it) => (
                    <div key={it.name}
                      className="flex items-center gap-2 flex-wrap rounded-lg border border-sky-200 bg-sky-50/50 px-3 py-2"
                      data-testid={`staff-c1-auto-${it.name}`}>
                      <p className="flex-1 min-w-[150px] text-[11px] font-semibold text-slate-700">{it.name}</p>
                      <span className="text-[10px] font-bold text-sky-800 bg-sky-100 border border-sky-300 rounded-full px-2 py-0.5">{it.score}/5</span>
                      <span className="text-[10px] text-slate-400">{it.basis}</span>
                    </div>
                  ))}
                </div>
              )}
              {evGroup && (
                <div className="mt-2 mr-8 space-y-1.5" data-testid={`staff-evidence-list-${k}`}>
                  <p className="text-[10px] font-bold text-slate-400">الشواهد المطلوبة:</p>
                  {evGroup.items.map((it) => {
                    const evs = it.evidence || [];
                    const key = `${evGroup.criterion}-${it.idx}`;
                    return (
                      <div key={it.idx} className="rounded-lg border bg-slate-50/60 px-3 py-2 space-y-1.5"
                        data-testid={`staff-evidence-item-${k}-${it.idx}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="flex-1 min-w-[150px] text-[11px] font-semibold text-slate-700">{it.name}</p>
                          <input type="file" accept="image/*,application/pdf" multiple className="hidden"
                            ref={(el) => { fileInputs.current[key] = el; }}
                            data-testid={`staff-evidence-input-${k}-${it.idx}`}
                            onChange={(e) => upload(evGroup.criterion, it.idx, e.target.files)} />
                          <Button size="sm" variant="outline" disabled={uploading === key}
                            onClick={() => fileInputs.current[key]?.click()}
                            data-testid={`staff-evidence-upload-${k}-${it.idx}`}
                            className="h-7 text-[11px] gap-1 px-2.5">
                            {uploading === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                            إضافة شاهد
                          </Button>
                        </div>
                        {evs.map((ev) => {
                          const st = EV_STATUS[ev.status];
                          return (
                            <div key={ev.id} className="flex items-center gap-2 flex-wrap border-t border-slate-200/70 pt-1.5"
                              data-testid={`staff-evidence-file-${ev.id}`}>
                              <p className="flex-1 min-w-[140px] text-[10px] text-slate-500 truncate" dir="auto">
                                {ev.original_name || "شاهد مرفوع"}</p>
                              <button onClick={() => viewFile(ev.id)} data-testid={`staff-evidence-view-${ev.id}`}
                                className="flex items-center gap-0.5 text-[11px] font-bold text-sky-700 hover:underline">
                                <FileText className="w-3.5 h-3.5" /> عرض
                              </button>
                              <span className={`text-[10px] font-bold border rounded-full px-2 py-0.5 ${st[1]}`}
                                data-testid={`staff-evidence-status-${ev.id}`}>{st[0]}</span>
                              {ev.status === "pending" && (
                                <button onClick={() => removeEvidence(ev.id)}
                                  data-testid={`staff-evidence-del-${ev.id}`}
                                  className="text-[11px] font-bold text-rose-600 hover:text-rose-800 hover:underline">
                                  حذف
                                </button>
                              )}
                              {ev.status === "rejected" && ev.note && (
                                <span className="text-[10px] text-rose-600">ملاحظة المدير: {ev.note}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl border p-3 flex items-center gap-3 flex-wrap">
        <span className="text-xs font-bold text-emerald-900 bg-emerald-100 border border-emerald-200 rounded-full px-3 py-1"
          data-testid="staff-eval-total">نتيجتي: {totals.total} / 5 ({totals.pct}٪)</span>
        {data.bonus_points > 0 && (
          <span className="text-xs font-bold text-amber-800 bg-amber-50 border border-amber-300 rounded-full px-3 py-1"
            data-testid="staff-eval-bonus"
            title="شواهد إضافية مقبولة فوق المطلوب — تُحتسب في المفاضلة بين المعلمين">
            نقاط مفاضلتي: +{data.bonus_points}
          </span>
        )}
        <Button onClick={save} disabled={saving} data-testid="staff-eval-save-btn"
          className="mr-auto h-10 text-xs font-bold gap-1.5 bg-emerald-900 hover:bg-emerald-950 text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} حفظ التقييم الذاتي
        </Button>
      </div>
    </div>
  );
}
